/**
 * THE breakpoint source of truth (spec §5, 2026-09-02 target-state design).
 * Consumers: useViewport (JS), tailwind.config.js (screens md/lg), the token
 * pipeline (generated breakpoints.css), and test viewport fixtures.
 *
 * Three bands, two thresholds. Orthogonal axes are NOT widths:
 * pointer/touch → `(pointer: coarse)` / useCoarsePointer; kiosk → server-verified
 * device identity (body.layout-kiosk), never viewport.
 * Do not add width constants elsewhere — the raw-px ratchet blocks it.
 */
export const BREAKPOINTS = {
  /** Below this width is `compact` (mobile web). */
  md: 768,
  /** Below this width (and ≥ md) is `medium`; at/above is `expanded`. */
  lg: 1024,
} as const;

export type BreakpointBand = 'compact' | 'medium' | 'expanded';

export function bandFor(width: number): BreakpointBand {
  if (width < BREAKPOINTS.md) return 'compact';
  if (width < BREAKPOINTS.lg) return 'medium';
  return 'expanded';
}

/**
 * Full Tailwind/theme breakpoint string map for CSS-in-JS consumers
 * (ThemeProvider, enhanced-theme-integration). md and lg are derived from
 * BREAKPOINTS so all three representations stay in sync. xs/sm/xl/2xl are
 * theme-only — they have no shell-layout semantics.
 */
export const THEME_BREAKPOINTS = {
  xs: '320px',
  sm: '640px',
  md: `${BREAKPOINTS.md}px`,
  lg: `${BREAKPOINTS.lg}px`,
  xl: '1280px',
  '2xl': '1536px',
} as const;
